import React, { useContext, useMemo } from 'react';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { useLocation } from '@thd-olt-component-react/router';

const ITEM_ID_PRODUCT = /(?:\/[\w-]+)?\/p(?:\/[\w-]+)?\/(\d+)/;
const ITEM_ID_CONFIG  = /\/config\/[a-f0-9-]+\/[^/]+\/(\d+)$/;
const PAINT_CONFIG_ID = /\/config\/([a-f0-9-]+)\//;

const resolveToken = (val, ctx) => {
  switch (val) {
    case '$channel':  return ctx.expContext.channel;
    case '$storeId':  return ctx.store.storeId;
    case '$storeZip': return ctx.store.storeZip;
    case '$pathname': return ctx.pathname;
    case '$itemId': {
      if (ctx.itemId) return ctx.itemId;
      const m = ITEM_ID_PRODUCT.exec(ctx.pathname) || ITEM_ID_CONFIG.exec(ctx.pathname);
      return m ? m[1] : '';
    }
    case '$paintReorderConfigId': {
      if (ctx.paintReorderConfigId) return ctx.paintReorderConfigId;
      const m = PAINT_CONFIG_ID.exec(ctx.pathname);
      return m ? m[1] : '';
    }
    case '$contentId': return ctx.contentId;
    case '$preloadImages': return ctx.preloadImages;
    default: return val;
  }
};

const hydrateValue = (val, ctx) => {
  if (typeof val === 'string') {
    return val.charCodeAt(0) === 36 ? resolveToken(val, ctx) : val;
  }
  if (val !== null && typeof val === 'object') {
    if (Array.isArray(val)) {
      const len = val.length;
      const out = new Array(len);
      for (let i = 0; i < len; i += 1) out[i] = hydrateValue(val[i], ctx);
      return out;
    }
    return hydrateProps(val, ctx);
  }
  return val;
};

// Single source of truth for `@namespace:value` override blocks (and for the docs chapter that
// renders the reference). Each namespace reads one value out of ExperienceContext; the block
// @<namespace>:<value> (value lowercased) is merged over its sibling props when it matches. To add
// a new kind of block, add an entry here - nothing else in the hook changes. Object order is
// precedence: later namespaces win over earlier ones on shared keys.
export const BLOCK_NAMESPACES = {
  channel: {
    reads: 'ExperienceContext.channel',
    description: 'Applies when the current channel matches (e.g. mobile, desktop, tablet).',
    example: '@channel:mobile',
    read: (expContext) => expContext.channel,
  },
  customer: {
    reads: 'ExperienceContext.customer.type',
    description: 'Applies when the customer type matches (e.g. b2b, b2c).',
    example: '@customer:b2b',
    read: (expContext) => expContext.customer && expContext.customer.type,
  },
  storefront: {
    reads: 'ExperienceContext.customer.thd_storefront.storeFrontType',
    description: 'Applies when the storefront type matches (e.g. exchange, eprocurement). Usually undefined.',
    example: '@storefront:exchange',
    read: (expContext) => expContext.customer
      && expContext.customer.thd_storefront
      && expContext.customer.thd_storefront.storeFrontType,
  },
};

const getActiveBlockKeys = (expContext) => Object.keys(BLOCK_NAMESPACES)
  .reduce((keys, namespace) => {
    const value = BLOCK_NAMESPACES[namespace].read(expContext);
    return typeof value === 'string' && value
      ? [...keys, `@${namespace}:${value.toLowerCase()}`]
      : keys;
  }, []);

// Keys that start with @ are override blocks - a signal for dynamic hydration, never regular props,
// so they are never copied to the output as-is. Blocks matching the current ExperienceContext (see
// BLOCK_NAMESPACES) are merged over their sibling props; every other @-prefixed key is dropped.
// This applies at every depth (nested objects and objects inside arrays go back through
// hydrateProps), so a block can sit inside any prop, and blocks can nest inside blocks. Value
// tokens ($storeId, ...) use $ and are unaffected; $-prefixed keys are ordinary props.
// The active block keys only depend on ExperienceContext, so compute them at most once per
// hydration (cached on ctx) rather than for every nested object.
const getBlockKeys = (ctx) => {
  if (!ctx.activeBlockKeys) ctx.activeBlockKeys = getActiveBlockKeys(ctx.expContext);
  return ctx.activeBlockKeys;
};

export const hydrateProps = (props, ctx) => {
  const out = {};
  let hasBlocks = false;
  const keys = Object.keys(props);
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    if (k.charCodeAt(0) === 64) hasBlocks = true;
    else out[k] = hydrateValue(props[k], ctx);
  }

  // Fast path: most objects contain no @ keys, so they skip all block work.
  if (!hasBlocks) return out;

  // out is a fresh object, so each matching block is written straight into it: the cost scales with
  // the block's size, not with the number of keys already in out.
  getBlockKeys(ctx).forEach((blockKey) => {
    const block = props[blockKey];
    if (block && typeof block === 'object' && !Array.isArray(block)) {
      Object.assign(out, hydrateProps(block, ctx));
    }
  });
  return out;
};

export const useHydratedProps = (component, opts = {}) => {
  const expContext = useContext(ExperienceContext);
  const store = useStore();
  const { pathname } = useLocation();

  // Everything hydration reads from context, as plain values. These are the memo dependencies, and
  // the memoized callback below rebuilds a minimal ctx from exactly these values, so hydration
  // cannot read a context field that is not a dependency - it would see undefined and fail tests
  // loudly instead of silently serving a stale cached result. If a new token or block reads
  // another context field, add it here.
  const { channel } = expContext;
  const customerType = expContext.customer && expContext.customer.type;
  const storefront = expContext.customer && expContext.customer.thd_storefront;
  const storeFrontType = storefront && storefront.storeFrontType;
  const { storeId, storeZip } = store;
  const resolvedPathname = pathname || (typeof window !== 'undefined' ? window.location.pathname : '');
  const {
    itemId, contentId, preloadImages, mergeArray
  } = opts;

  return useMemo(() => {
    const ctx = {
      store: { storeId, storeZip },
      expContext: {
        channel,
        customer: { type: customerType, thd_storefront: { storeFrontType } },
      },
      pathname: resolvedPathname,
      itemId,
      contentId,
      preloadImages
    };

    if (Array.isArray(component)) {
      const ret = component.map((comp) => hydrateProps(comp.props, ctx));
      return mergeArray
        ? ret.reduce((acc, cur) => ({ ...acc, ...cur }), {})
        : ret;
    }
    return hydrateProps(component.props, ctx);
  }, [
    component, mergeArray, itemId, contentId, preloadImages,
    channel, customerType, storeFrontType, storeId, storeZip, resolvedPathname
  ]);
};